import { lazy } from 'react';
import { Route } from 'react-router-dom';
import { pageNames } from '@common/routes/pageNames';
import ClosedAgreement from '@dues/pages/Partner/ClosedAgreement';
import { CheckoutGroup, routes as renegKitRoutes } from '@libs/renegkit';
import { useUserStore } from '@libs/acp/store/user/useUserStore';
import { identityUpdateContactSendCode, identityUpdateContactVerifyCode } from '@libs/acp/apis/updateAccount/updateAccountAPI';
import { maskCellphone } from '@libs/utils/masks/cellphone';
import { useCheckoutSessionConfig } from '@dues/hooks/useCheckoutSessionConfig';
import useGTM from '@common/hooks/useGTM';

const MyDebts = lazy(() => import('@dues/pages/MyDebts'));
const Debts = lazy(() => import('@dues/pages/Debts'));

const Confirmation = lazy(() => import('@dues/pages/Partner/Confirmation'));
const ClosedAgreementError = lazy(() => import('@dues/pages/Partner/ClosedAgreementError'));

const Agreement = lazy(() => import('@dues/pages/RKAgreement'));
const AgreementComposition = lazy(() => import('@dues/pages/RKAgreement/Composition'));
const AgreementConditions = lazy(() => import('@dues/pages/RKAgreement/Conditions'));
const MyAgreements = lazy(() => import('@dues/pages/RKMyAgreements'));

const BilletNotFoundError = lazy(() => import('@dues/components/BilletNotFoundError'));

const NegativeDebtsPage = lazy(() => import('@dues/pages/NegativeDebts'));

export function Router() {

  // #region rk checkout configs and tracking - TEMP: improve
  const { authenticatedUser, addAuthenticatedUser } = useUserStore();
  const customer = authenticatedUser?.acp?.customer;
  const checkoutSessionConfig = useCheckoutSessionConfig();
  const trackGTM = useGTM();

  const checkoutContactConfig = {
    isNCADCheckout: customer?.required_updates?.includes('account_ncad'),
    isNeverEngaged: false, // TODO: check
    userEmail: customer?.email ?? '',
    userPhone: maskCellphone(customer?.phone ?? ''),
    onSendCode: async (payload: { type: 'celular' | 'e-mail'; contact: string }) => {
      const contactType = payload.type === 'celular' ? 'phone' : 'email';
      return identityUpdateContactSendCode({ type: contactType, contact: payload.contact });
    },
    onVerifyCode: async (payload: { type: 'celular' | 'e-mail'; contact: string; token: string }) => {
      const isPhoneFlow = payload.type === 'celular';
      const contactType = isPhoneFlow ? 'phone' : 'email';
      await identityUpdateContactVerifyCode({ type: contactType, token: payload.token });
      addAuthenticatedUser(
        {
          ...authenticatedUser.acp,
          customer: {
            ...customer,
            ...(isPhoneFlow ? { phone: payload.contact } : { email: payload.contact }),
          },
        },
        'acp',
      );
    },
  };

  // #endregion

  return (
    <>
      <Route path={pageNames.myDebts.path} element={<MyDebts />} />
      <Route path={pageNames.debts.path} element={<Debts />} />
      <Route path={pageNames.confirmation.path} element={<Confirmation />} />
      <Route path={pageNames.agreementClosed.path} element={<ClosedAgreement />} />
      <Route path={pageNames.agreementClosedError.path} element={<ClosedAgreementError />} />
      {...CheckoutGroup({ contactConfig: checkoutContactConfig, sessionConfig: checkoutSessionConfig, trackGTM: trackGTM })}

      <Route path={renegKitRoutes.myAgreements.path} element={<MyAgreements />} />
      <Route path={renegKitRoutes.agreement.path} element={<Agreement />} />
      <Route path={renegKitRoutes.agreementComposition.path} element={<AgreementComposition />} />
      <Route path={renegKitRoutes.agreementConditions.path} element={<AgreementConditions />} />

      <Route path={renegKitRoutes.myAgreements.path} element={<MyAgreements />} />
      <Route path={pageNames.billetNotFoundError.path} element={<BilletNotFoundError />} />
      <Route path={pageNames.negativeDebts.path} element={<NegativeDebtsPage />} />
    </>
  );
}
